import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {
  LIST_MASONRY_PAGINATION_BUTTON_GAP,
  LIST_MASONRY_PAGINATION_BUTTON_GAP_MOBILE,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {ListPaginationModeType, WindowModeType} from 'types'
import {useWindowMode} from 'windows'
import {
  Icon,
  ListMasonryPaginationButtonControls,
  ListMasonryPaginationButtonPage,
  ListMasonryPaginationGapMarker,
} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopExplorePaginationProps} from './GiftShopExplorePaginationProps'

interface StyledPaginationContainerProps {
  mode?: WindowModeType
}

const StyledPaginationContainer = styled.div<StyledPaginationContainerProps>`
  ${({mode}) => ({
    placeSelf: mode === 'website' ? 'flex-end' : undefined,
  })}
  width: fit-content;
  height: fit-content;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: ${convertUnit(LIST_MASONRY_PAGINATION_BUTTON_GAP)};
  padding: ${convertUnit(LIST_MASONRY_PAGINATION_BUTTON_GAP)};
  place-self: flex-end;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    place-self: center;
    gap: ${convertUnit(LIST_MASONRY_PAGINATION_BUTTON_GAP_MOBILE)};
    order: 1;
  }
`

export default function GiftShopExplorePagination({
  pageCount,
  statePage,
  onClickPage,
}: GiftShopExplorePaginationProps) {
  const mode = useWindowMode()
  const [page, setPage] = statePage

  const paginationCalcs = useMemo(() => {
    const compactMaxPages = mode !== 'mobile' ? 9 : 7
    const pageDiff = mode !== 'mobile' ? 2 : 1
    const middleMinThreshold = compactMaxPages - (pageDiff + 1)
    const middleMaxThreshold = pageCount - (compactMaxPages - 4)
    const paginationMode: ListPaginationModeType = (() => {
      if (pageCount <= compactMaxPages) return 'compact'
      if (page < middleMinThreshold) return 'start'
      if (middleMaxThreshold - (mode !== 'mobile' ? 0 : 1) >= page)
        return 'middle'

      return 'end'
    })()

    return {
      compactMaxPages,
      pageDiff,
      middleMinThreshold,
      middleMaxThreshold,
      paginationMode,
    }
  }, [mode, page, pageCount])

  const {
    compactMaxPages,
    middleMaxThreshold,
    pageDiff,
    paginationMode,
  } = paginationCalcs

  const safePage = useCallback(
    (p: number) => {
      let value = p
      if (value < 1) value = 1
      if (value > pageCount) value = pageCount

      return value
    },
    [pageCount],
  )

  const handleMovePage = useCallback(
    (newPage: number) => {
      setPage(safePage(newPage))
      onClickPage && onClickPage()
    },
    [onClickPage, safePage, setPage],
  )

  const handleRenderPaginationPageButtons = useMemo(() => {
    switch (paginationMode) {
      case 'compact':
        return Array.from({length: pageCount}).map((_, idx) => (
          <ListMasonryPaginationButtonPage
            page={page}
            setPage={handleMovePage}
            destination={idx + 1}
            label={idx === 0 ? 'All' : undefined}
          />
        ))
      case 'start':
        return (
          <>
            {Array.from({length: compactMaxPages - 2}).map((_, idx) => (
              <ListMasonryPaginationButtonPage
                page={page}
                setPage={handleMovePage}
                destination={idx + 1}
                label={idx === 0 ? 'All' : undefined}
              />
            ))}
            <ListMasonryPaginationGapMarker />
            <ListMasonryPaginationButtonPage
              page={page}
              setPage={handleMovePage}
              destination={pageCount}
            />
          </>
        )
      case 'end':
        return (
          <>
            <ListMasonryPaginationButtonPage
              page={page}
              setPage={handleMovePage}
              destination={1}
              label="All"
            />
            <ListMasonryPaginationGapMarker />
            {Array.from({length: compactMaxPages - 2}).map((_, idx) => (
              <ListMasonryPaginationButtonPage
                page={page}
                setPage={handleMovePage}
                destination={idx + middleMaxThreshold - 1}
              />
            ))}
          </>
        )
      case 'middle':
        return (
          <>
            <ListMasonryPaginationButtonPage
              page={page}
              setPage={handleMovePage}
              destination={1}
              label="All"
            />
            <ListMasonryPaginationGapMarker />
            {Array.from({length: compactMaxPages - 4}).map((_, idx) => (
              <ListMasonryPaginationButtonPage
                page={page}
                setPage={handleMovePage}
                destination={idx + (page - pageDiff)}
              />
            ))}
            <ListMasonryPaginationGapMarker />
            <ListMasonryPaginationButtonPage
              page={page}
              setPage={handleMovePage}
              destination={pageCount}
            />
          </>
        )
    }
  }, [
    paginationMode,
    pageCount,
    compactMaxPages,
    page,
    handleMovePage,
    middleMaxThreshold,
    pageDiff,
  ])

  const handleRenderPaginationMenu = useMemo(
    () =>
      pageCount > 0 ? (
        <StyledPaginationContainer mode={mode}>
          <ListMasonryPaginationButtonControls
            onClick={() => handleMovePage(page - 5)}>
            <Icon type="double-arrow-left" />
          </ListMasonryPaginationButtonControls>
          <ListMasonryPaginationButtonControls
            onClick={() => handleMovePage(page - 1)}>
            <Icon type="chevron-left" />
          </ListMasonryPaginationButtonControls>
          {handleRenderPaginationPageButtons}
          <ListMasonryPaginationButtonControls
            onClick={() => handleMovePage(page + 1)}>
            <Icon type="chevron-right" />
          </ListMasonryPaginationButtonControls>
          <ListMasonryPaginationButtonControls
            onClick={() => handleMovePage(page + 5)}>
            <Icon type="double-arrow-right" />
          </ListMasonryPaginationButtonControls>
        </StyledPaginationContainer>
      ) : (
        <></>
      ),
    [handleMovePage, handleRenderPaginationPageButtons, mode, page, pageCount],
  )

  return handleRenderPaginationMenu
}
